/**
 * Server-sent events reader for the Auto stream (plans/01 sections 3.5 and 3.6; port of the GolfTec parser).
 *
 * It reads with `fetch` so the normal `Authorization: Bearer` header can be sent: a token never goes in a URL
 * (Rule 13 section 4). The parsing functions are pure, so they can be unit tested without a network.
 *
 * Wire format (fixed by `app/core/sse.py`): frames are separated by a blank line; a frame has optional `event:`
 * and `id:` lines and one or more `data:` lines; lines starting with `:` are comments (keepalive heartbeats).
 */

export interface SseFrame {
  /** SSE `event:` name; "message" when absent. */
  event: string
  /** SSE `id:` value, when present. */
  id: string | null
  /** Parsed JSON `data:` payload, or the raw string when it is not JSON. */
  data: unknown
}

export interface ReadSseOptions {
  headers?: HeadersInit
  signal?: AbortSignal
  onFrame: (frame: SseFrame) => void
  /** Called for each `:` comment frame (a keepalive), so a caller can tell the connection is alive. */
  onHeartbeat?: () => void
}

/** A non-2xx answer from the stream endpoint; `status` tells the caller whether to refresh its token (401). */
export class SseHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`The stream answered HTTP ${status}`)
    this.name = 'SseHttpError'
    this.status = status
  }
}

type RawFrame = { type: 'event'; event: string; id: string | null; data: string } | { type: 'comment' }

/** Parses one raw frame (the text between two blank lines). Returns null for a frame with no data and no comment. */
export function parseSseFrame(raw: string): RawFrame | null {
  const dataLines: string[] = []
  let sawComment = false
  let event = 'message'
  let id: string | null = null

  for (const line of raw.split(/\r\n|\r|\n/)) {
    if (line === '') continue
    if (line.startsWith(':')) {
      sawComment = true
      continue
    }
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'data') dataLines.push(value)
    else if (field === 'event') event = value || 'message'
    else if (field === 'id') id = value
  }

  if (dataLines.length > 0) return { type: 'event', event, id, data: dataLines.join('\n') }
  return sawComment ? { type: 'comment' } : null
}

/**
 * Incremental splitter: feed it decoded text chunks in arrival order; it returns the complete frames so far and
 * keeps a partial frame buffered. Handles `\n\n`, `\r\n\r\n` and `\r\r` separators.
 */
export function createSseParser() {
  let buffer = ''
  const separator = /\r\n\r\n|\n\n|\r\r/

  return {
    push(chunk: string): RawFrame[] {
      buffer += chunk
      const frames: RawFrame[] = []
      let match = separator.exec(buffer)
      while (match) {
        const frame = parseSseFrame(buffer.slice(0, match.index))
        buffer = buffer.slice(match.index + match[0].length)
        if (frame) frames.push(frame)
        match = separator.exec(buffer)
      }
      return frames
    },
    /** Parses what is left when the stream closes without a final blank line. */
    flush(): RawFrame[] {
      const frame = buffer.trim() ? parseSseFrame(buffer) : null
      buffer = ''
      return frame ? [frame] : []
    },
  }
}

function decodeData(data: string): unknown {
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

/**
 * Reads an SSE response to its end, calling `onFrame` for each event frame. Resolves when the server closes the
 * stream (the Auto stream rotates after a few minutes, so a caller reconnects with the last id). Rejects with
 * `SseHttpError` on a non-2xx status, and with an `AbortError` when `signal` aborts.
 */
export async function readSseStream(url: string, options: ReadSseOptions): Promise<void> {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'text/event-stream')
  const response = await fetch(url, { method: 'GET', headers, signal: options.signal, cache: 'no-store' })
  if (!response.ok) throw new SseHttpError(response.status)
  if (!response.body) return

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const parser = createSseParser()

  const dispatch = (frames: RawFrame[]) => {
    for (const frame of frames) {
      if (frame.type === 'comment') options.onHeartbeat?.()
      else options.onFrame({ event: frame.event, id: frame.id, data: decodeData(frame.data) })
    }
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        dispatch(parser.flush())
        return
      }
      dispatch(parser.push(decoder.decode(value, { stream: true })))
    }
  } finally {
    reader.cancel().catch(() => undefined)
  }
}
